// CodeRunnerModal.js - Popup code editor for running Python code
// Used for both "Try It" (pre-filled) and "Scratch Pad" (empty) modes

import React, { useState, useEffect, useRef } from 'react';
import { PYTHON_INTERPRETER_URL, isDemoMode } from '../config';
import VisualDebugger from './VisualDebugger';

// Helper to normalize Python code indentation
// Only fixes code that has NO indentation at all (all lines start at column 0)
// If code already has some indentation, we preserve it as-is (LLM got it right)
const normalizeIndentation = (code) => {
  if (!code || !code.trim()) return code;
  
  const lines = code.split('\n');
  
  // Check if ANY non-empty line has leading whitespace
  // If so, the code already has indentation - return as-is
  const hasExistingIndent = lines.some(line => {
    const trimmed = line.trim();
    return trimmed.length > 0 && line.startsWith(' ');
  });
  
  if (hasExistingIndent) {
    return code; // Preserve original indentation
  }
  
  // Code has NO indentation at all - try to fix it
  const result = [];
  let currentIndentLevel = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();
    
    if (trimmed.length === 0) {
      result.push('');
      continue;
    }
    
    // Remove comments to check for block openers
    const codeWithoutComment = trimmed.replace(/#.*$/, '').trim();
    
    // Check if current line is a dedent keyword
    const isDedentKeyword = /^(else:|elif\s|except:|except\s|finally:)/.test(trimmed);
    
    if (isDedentKeyword && currentIndentLevel >= 4) {
      currentIndentLevel -= 4;
    }
    
    result.push(' '.repeat(currentIndentLevel) + trimmed);
    
    if (codeWithoutComment.endsWith(':')) {
      currentIndentLevel += 4;
    }
  }
  
  return result.join('\n');
};

const CodeRunnerModal = ({ initialCode = '', onClose, title = 'Code Runner', onSendToChat = null }) => {
  // For Scratch Pad (empty initialCode), load from localStorage
  const isScratchPad = title === 'Scratch Pad' && initialCode === '';
  const savedCode = isScratchPad ? localStorage.getItem('scratchPadCode') || '' : normalizeIndentation(initialCode);
  
  const [code, setCode] = useState(savedCode);
  const [output, setOutput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [executionId, setExecutionId] = useState(null);
  const [isAwaitingInput, setIsAwaitingInput] = useState(false);
  const [inputValue, setInputValue] = useState('');

  // --- Visualizer state ---
  const [showVisualizer, setShowVisualizer] = useState(false);
  const [traceSteps, setTraceSteps] = useState([]);
  const [isTracing, setIsTracing] = useState(false);
  const [showInputModal, setShowInputModal] = useState(false);
  const [inputModalValues, setInputModalValues] = useState('');
  const [pendingTraceCode, setPendingTraceCode] = useState('');
  const textareaRef = useRef(null);
  const outputRef = useRef(null);

  // --- Draggable floating window state (Scratch Pad only) ---
  const [position, setPosition] = useState(() => {
    // Default: bottom-right corner with some padding
    return {
      x: window.innerWidth - 480,
      y: window.innerHeight - 520
    };
  });
  const [size, setSize] = useState({ width: 450, height: 480 });
  
  // --- Resizable "Try It" modal state ---
  const [tryItSize, setTryItSize] = useState({ width: 700, height: 50 }); // height in vh
  
  const isDragging = useRef(false);
  const isResizing = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Dragging handlers
  const handleMouseDown = (e) => {
    if (!isScratchPad) return;
    // Only drag from the header bar
    isDragging.current = true;
    dragOffset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y
    };
    e.preventDefault();
  };

  const handleResizeMouseDown = (e) => {
    if (!isScratchPad) return;
    isResizing.current = true;
    dragOffset.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height
    };
    e.preventDefault();
    e.stopPropagation();
  };

  // Resize handler for "Try It" modal
  const handleTryItResizeMouseDown = (e) => {
    if (isScratchPad) return;
    isResizing.current = true;
    dragOffset.current = {
      x: e.clientX,
      y: e.clientY,
      width: tryItSize.width,
      height: tryItSize.height
    };
    e.preventDefault();
    e.stopPropagation();
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isScratchPad) {
        if (isDragging.current) {
          const newX = Math.max(0, Math.min(window.innerWidth - 100, e.clientX - dragOffset.current.x));
          const newY = Math.max(0, Math.min(window.innerHeight - 40, e.clientY - dragOffset.current.y));
          setPosition({ x: newX, y: newY });
        }
        if (isResizing.current) {
          const dx = e.clientX - dragOffset.current.x;
          const dy = e.clientY - dragOffset.current.y;
          setSize({
            width: Math.max(320, dragOffset.current.width + dx),
            height: Math.max(250, dragOffset.current.height + dy)
          });
        }
      } else {
        // "Try It" modal resizing
        if (isResizing.current) {
          const dx = e.clientX - dragOffset.current.x;
          const dy = e.clientY - dragOffset.current.y;
          // Convert pixel change to vh for height
          const vhChange = (dy / window.innerHeight) * 100;
          setTryItSize({
            width: Math.max(500, Math.min(1400, dragOffset.current.width + dx)),
            height: Math.max(30, Math.min(90, dragOffset.current.height + vhChange))
          });
        }
      }
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      isResizing.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isScratchPad, position.x, position.y]);

  // Save Scratch Pad code to localStorage whenever it changes
  useEffect(() => {
    if (isScratchPad) {
      localStorage.setItem('scratchPadCode', code);
    }
  }, [code, isScratchPad]);

  // Focus textarea on mount
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  // Auto-scroll output
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [output]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Escape to close
      if (e.key === 'Escape' && !isRunning) {
        onClose();
      }
      // Ctrl/Cmd + Enter to run
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        if (!isRunning) {
          handleRun();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isRunning, code]);

  const handleRun = async () => {
    if (!code.trim()) {
      setOutput('⚠️ Please enter some code to run.');
      return;
    }

    if (isDemoMode()) {
      setOutput('🔒 Code execution is locked in demo mode.\n\nWith an account you can run Python live in this sandbox.\n\nExample:\n  >>> print("Hello, World!")\n  Hello, World!\n\n  >>> for i in range(3):\n  ...     print(i)\n  0\n  1\n  2\n\nThe sandbox handles input(), loops, and errors — and the AI tutor can review your code.\n\n✨ Sign up to run Python live!');
      return;
    }

    setIsRunning(true);
    setOutput('▶️ Running...\n');
    setExecutionId(null);
    setIsAwaitingInput(false);

    try {
      const response = await fetch(`${PYTHON_INTERPRETER_URL}/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ code: code })
      });

      const data = await response.json();
      
      // Handle different response formats from the Python interpreter
      const outputText = data.stdout || data.output || '';
      const errorText = data.stderr || data.error || '';

      if (data.status === 'waiting_for_input' || data.status === 'awaiting_input') {
        setExecutionId(data.execution_id);
        setIsAwaitingInput(true);
        setIsRunning(true);
        setOutput(prev => prev.replace('▶️ Running...\n', '') + outputText + '\n📝 Waiting for input...');
      } else if (data.status === 'completed' || data.status === 'finished') {
        const result = outputText || (errorText ? '' : '✅ Code executed successfully (no output)');
        const errors = errorText ? '\n❌ ' + errorText : '';
        setOutput(prev => prev.replace('▶️ Running...\n', '') + result + errors);
      } else if (data.status === 'error') {
        setOutput(prev => prev.replace('▶️ Running...\n', '') + '❌ Error:\n' + (errorText || outputText || 'Unknown error'));
      } else {
        // Fallback for unexpected status
        setOutput(prev => prev.replace('▶️ Running...\n', '') + (outputText || errorText || '✅ Done'));
      }
    } catch (error) {
      setOutput('❌ Connection error: ' + error.message);
    } finally {
      if (!isAwaitingInput) {
        setIsRunning(false);
      }
    }
  };

  const handleSendInput = async () => {
    if (!executionId || !inputValue.trim()) return;
    if (isDemoMode()) return;

    try {
      // Use /resume endpoint (same as main App.js code runner)
      const response = await fetch(`${PYTHON_INTERPRETER_URL}/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ 
          execution_id: executionId, 
          input: inputValue 
        })
      });

      const data = await response.json();
      const userInput = inputValue;
      setInputValue('');
      
      // Handle response - server's stdout is the absolute source of truth
      const newStdout = data.stdout || '';
      const errorText = data.stderr || '';

      if (data.execution_id) {
        // Still waiting for more input
        setExecutionId(data.execution_id);
        setOutput(newStdout + '\n📝 Waiting for input...');
      } else {
        // Execution completed
        setOutput(newStdout + (errorText ? '\n❌ ' + errorText : ''));
        setIsAwaitingInput(false);
        setIsRunning(false);
        setExecutionId(null);
      }
    } catch (error) {
      setOutput(prev => prev + '\n❌ Input error: ' + error.message);
      setIsAwaitingInput(false);
      setIsRunning(false);
    }
  };

  const handleClear = () => {
    setCode('');
    setOutput('');
    setExecutionId(null);
    setIsAwaitingInput(false);
    setIsRunning(false);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleStop = async () => {
    if (executionId) {
      try {
        await fetch(`${PYTHON_INTERPRETER_URL}/stop`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
          body: JSON.stringify({ execution_id: executionId })
        });
      } catch (e) {
        // Ignore stop errors
      }
    }
    setIsRunning(false);
    setIsAwaitingInput(false);
    setExecutionId(null);
    setOutput(prev => prev + '\n⏹️ Execution stopped.');
  };

  // ── Visualizer helpers ────────────────────────────────────────────────────

  /** Count input() calls in source code, ignoring commented lines */
  const countInputCalls = (src) => {
    const uncommented = src.split('\n').map(line => {
      const t = line.trimStart();
      if (t.startsWith('#')) return '';
      const ci = line.indexOf('#');
      return ci >= 0 ? line.slice(0, ci) : line;
    }).join('\n');
    const matches = uncommented.match(/\binput\s*\(/g);
    return matches ? matches.length : 0;
  };

  /** Open the step-by-step visualizer */
  const handleVisualize = () => {
    if (!code.trim()) {
      setOutput('🔒 Step-by-step visualizer is locked in demo mode.\n\nWith an account you can step through your Python code line by line,\nwatching each variable change in real time.\n\n✨ Sign up to unlock!');
      return;
    }
    if (isDemoMode()) {
      setOutput('🔒 Step-by-step visualizer is locked in demo mode.\n\nWith an account you can step through your Python code line by line,\nwatching each variable change in real time.\n\n✨ Sign up to unlock!');
      return;
    }
    const inputCount = countInputCalls(code);
    if (inputCount > 0) {
      setPendingTraceCode(code);
      setInputModalValues('');
      setShowInputModal(true);
    } else {
      runTrace(code, []);
    }
  };

  const runTrace = async (src, inputs) => {
    setIsTracing(true);
    setShowInputModal(false);
    try {
      const res = await fetch(`${PYTHON_INTERPRETER_URL}/trace`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(localStorage.getItem('jwtToken') ? { 'Authorization': `Bearer ${localStorage.getItem('jwtToken')}` } : {}) },
        body: JSON.stringify({ code: src, inputs })
      });
      const data = await res.json();
      if (data.steps && data.steps.length > 0) {
        setTraceSteps(data.steps);
        setShowVisualizer(true);
      } else {
        setOutput('⚠️ Visualizer: no executable steps found. Make sure your code has at least one statement.');
      }
    } catch (err) {
      setOutput(`⚠️ Visualizer error: ${err.message}`);
    } finally {
      setIsTracing(false);
    }
  };

  // --- Floating Scratch Pad wrapper vs. Modal overlay ---
  const wrapperStyle = isScratchPad
    ? {
        // Floating window — no backdrop, see-through behind
        position: 'fixed',
        top: position.y,
        left: position.x,
        width: size.width,
        height: size.height,
        zIndex: 10000,
        pointerEvents: 'auto',
      }
    : {
        // Full-screen modal overlay for "Try It"
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '100px',
        zIndex: 10000
      };

  const cardStyle = isScratchPad
    ? {
        backgroundColor: '#161b22',
        borderRadius: '12px',
        border: '2px solid #00d4aa',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 12px 48px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(0, 212, 170, 0.15)',
        overflow: 'hidden',
        position: 'relative',
      }
    : {
        backgroundColor: '#161b22',
        borderRadius: '12px',
        border: '2px solid #a89860',
        width: `${tryItSize.width}px`,
        maxWidth: '95%',
        height: `${tryItSize.height}vh`,
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        position: 'relative',
      };

  return (
    <>
    <div style={wrapperStyle} ref={containerRef}>
      <div style={cardStyle}>
        {/* Header - draggable for Scratch Pad */}
        <div
          onMouseDown={isScratchPad ? handleMouseDown : undefined}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '10px 14px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#21262d',
            borderRadius: '12px 12px 0 0',
            cursor: isScratchPad ? 'grab' : 'default',
            userSelect: 'none',
            flexShrink: 0,
          }}
        >
          <span style={{ 
            color: isScratchPad ? '#00d4aa' : '#58a6ff', 
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
          }}>
            🐍 {title}
          </span>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {!isScratchPad && (
              <span style={{ color: '#8b949e', fontSize: '12px' }}>
                Ctrl+Enter to run • Esc to close
              </span>
            )}
            {isScratchPad && (
              <span style={{ color: '#8b949e', fontSize: '11px', marginRight: '4px' }}>
                ⌘+Enter to run
              </span>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: '#f0f6fc',
                fontSize: '20px',
                cursor: 'pointer',
                padding: '2px 6px',
                borderRadius: '4px',
                lineHeight: 1,
              }}
              onMouseOver={(e) => e.target.style.color = '#f85149'}
              onMouseOut={(e) => e.target.style.color = '#f0f6fc'}
            >
              ×
            </button>
          </div>
        </div>

        {/* Code Editor */}
        <div style={{ padding: '12px', flex: '1', minHeight: '0', display: 'flex', flexDirection: 'column' }}>
          <textarea
            ref={textareaRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="# Write your Python code here..."
            spellCheck={false}
            style={{
              width: '100%',
              flex: 1,
              minHeight: '80px',
              backgroundColor: '#0d1117',
              color: '#ffffff',
              border: '1px solid #30363d',
              borderRadius: '8px',
              padding: '12px',
              fontFamily: 'Consolas, Monaco, "Courier New", monospace',
              fontSize: '20px',
              lineHeight: '1.5',
              resize: isScratchPad ? 'none' : 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '8px',
          padding: '0 12px 12px 12px',
          flexWrap: 'wrap',
          flexShrink: 0,
        }}>
          <button
            onClick={handleRun}
            disabled={isRunning && !isAwaitingInput}
            style={{
              padding: '8px 20px',
              backgroundColor: isRunning && !isAwaitingInput ? '#21262d' : '#238636',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: isRunning && !isAwaitingInput ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            ▶️ Run
          </button>
          {isRunning && (
            <button
              onClick={handleStop}
              style={{
                padding: '8px 16px',
                backgroundColor: '#da3633',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              ⏹️ Stop
            </button>
          )}
          {onSendToChat && (
            <button
              onClick={() => {
                if (isDemoMode()) {
                  setOutput('🔒 Sending code to the AI tutor is locked in demo mode.\n\nWith an account you can get personalized help, hints, and step-by-step guidance on your Python code.\n\n✨ Sign up to unlock!');
                  return;
                }
                const message = `Here's my code:\n\n\`\`\`python\n${code}\n\`\`\`\n\nCan you help me understand or fix this?`;
                onSendToChat(message);
                onClose();
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: '#21262d',
                color: '#58a6ff',
                border: '1px solid #6e6450',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              💬 Send to Tutor
            </button>
          )}
          <button
            onClick={handleVisualize}
            disabled={isTracing}
            title="Step through code line by line"
            style={{
              padding: '8px 16px',
              backgroundColor: isTracing ? '#21262d' : '#1d3a5f',
              color: isTracing ? '#4b5563' : '#79c0ff',
              border: isTracing ? '1px solid #388bfd55' : '1px solid rgba(255, 240, 150, 0.75)',
              borderRadius: '6px',
              cursor: isTracing ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
            }}
          >
            {isTracing ? '⏳ Tracing...' : '🔍 Visualize'}
          </button>
          <button
            onClick={handleClear}
            style={{
              padding: '8px 16px',
              backgroundColor: '#21262d',
              color: '#c9d1d9',
              border: '1px solid #6e6450',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            🗑️ Clear
          </button>
        </div>

        {/* Output Area */}
        <div style={{
          padding: '0 12px 12px 12px',
          flexShrink: 0,
        }}>
          <div style={{
            backgroundColor: '#0d1117',
            border: '1px solid #30363d',
            borderRadius: '8px',
            padding: '12px',
            minHeight: isScratchPad ? '60px' : '100px',
            maxHeight: isScratchPad ? '120px' : '200px',
            overflowY: 'auto',
            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
            fontSize: '16px',
            color: '#e6d88a',
            whiteSpace: 'pre-wrap',
            textAlign: 'left'
          }} ref={outputRef}>
            {output || <span style={{ color: '#8b949e', textAlign: 'left', display: 'block' }}>Output will appear here...</span>}
          </div>
        </div>

        {/* Input field when waiting for input */}
        {isAwaitingInput && (
          <div style={{
            display: 'flex',
            gap: '8px',
            padding: '0 12px 12px 12px',
            flexShrink: 0,
          }}>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSendInput()}
              placeholder="Enter input and press Enter..."
              autoFocus
              style={{
                flex: 1,
                padding: '10px 12px',
                backgroundColor: '#0d1117',
                color: '#c9d1d9',
                border: '2px solid #58a6ff',
                borderRadius: '6px',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button
              onClick={handleSendInput}
              style={{
                padding: '10px 16px',
                backgroundColor: '#238636',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Send
            </button>
          </div>
        )}

        {/* Resize handle - bottom right corner (Scratch Pad only) */}
        {isScratchPad && (
          <div
            onMouseDown={handleResizeMouseDown}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '20px',
              height: '20px',
              cursor: 'nwse-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#484f58',
              fontSize: '10px',
              userSelect: 'none',
              borderRadius: '0 0 12px 0',
            }}
            title="Drag to resize"
          >
            ⟋
          </div>
        )}

        {/* Resize handle - bottom right corner (Try It modal) */}
        {!isScratchPad && (
          <div
            onMouseDown={handleTryItResizeMouseDown}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: '24px',
              height: '24px',
              cursor: 'nwse-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#a89860',
              fontSize: '12px',
              userSelect: 'none',
              borderRadius: '0 0 12px 0',
              backgroundColor: 'rgba(168, 152, 96, 0.1)',
            }}
            title="Drag to resize"
          >
            ⟋
          </div>
        )}
      </div>
    </div>

    {/* ── Pre-supply inputs modal ── */}
    {showInputModal && (
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.75)', zIndex: 12000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{
          backgroundColor: '#161b22', border: '1px solid rgba(255, 240, 150, 0.6)',
          borderRadius: '12px', padding: '0', width: '780px', maxWidth: '95%', overflow: 'hidden',
        }}>
          <div style={{ backgroundColor: 'rgba(255,240,150,0.07)', borderBottom: '1px solid rgba(255,240,150,0.2)', padding: '20px 28px' }}>
            <h3 style={{ color: '#f0e68c', margin: 0, fontSize: '22px' }}>
              📥 Supply input() values
            </h3>
            <p style={{ color: '#c9ba7a', fontSize: '17px', marginBottom: '0', marginTop: '8px', lineHeight: 1.6 }}>
              Your code uses <code style={{ color: '#79c0ff' }}>input()</code>. Enter one value per line — supply as many as needed (even if the count depends on earlier inputs).
            </p>
          </div>
          <div style={{ padding: '20px 28px 28px' }}>
          <textarea
            autoFocus
            value={inputModalValues}
            onChange={e => setInputModalValues(e.target.value)}
            placeholder={`value 1\nvalue 2\nvalue 3\n...`}
            rows={16}
            style={{
              width: '100%', padding: '10px 14px',
              backgroundColor: '#0d1117', color: '#c9d1d9',
              border: '1px solid rgba(255, 240, 150, 0.5)', borderRadius: '6px',
              fontFamily: 'monospace', fontSize: '22px', outline: 'none',
              boxSizing: 'border-box', resize: 'vertical',
            }}
          />
          <p style={{ color: '#c9ba7a', fontSize: '17px', marginTop: '10px', marginBottom: '0', lineHeight: 1.5 }}>
            💡 <strong style={{ color: '#e8d48a' }}>Tip:</strong> Run the code first to see what it asks for.
            If your code uses <code style={{ color: '#79c0ff' }}>random</code>, the visualizer uses fixed values — run it once to discover them, then supply the matching inputs here.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
            <button
              onClick={() => runTrace(pendingTraceCode, inputModalValues.split('\n'))}
              style={{
                flex: 1, padding: '9px', backgroundColor: '#238636',
                color: '#fff', border: 'none', borderRadius: '6px',
                cursor: 'pointer', fontWeight: 700,
              }}
            >
              🔍 Run Visualizer
            </button>
            <button
              onClick={() => setShowInputModal(false)}
              style={{
                padding: '9px 18px', backgroundColor: '#21262d',
                color: '#8b949e', border: '1px solid rgba(255, 240, 150, 0.35)', borderRadius: '6px',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
          </div>
        </div>
      </div>
    )}

    {/* ── Visual Debugger overlay ── */}
    {showVisualizer && traceSteps.length > 0 && (
      <VisualDebugger
        code={code}
        steps={traceSteps}
        onClose={() => setShowVisualizer(false)}
      />
    )}
  </>
  );
};

export default CodeRunnerModal;
